import React, { useCallback, useEffect, useState } from 'react';
import PlacesAutocompleteInput from '../../components/PlacesAutocompleteInput';
import config from '../../config';
import { toast } from 'react-toastify';

const API_BASE = config.opsApiBase.replace('/api/v1/ops', '');
const CLASS_OPTIONS = Array.from({ length: 18 }, (_, i) => i + 1);

function normalizePhoneForDb(phone) {
  let digits = String(phone || '').replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
  if (digits.startsWith('91') && digits.length === 12) return digits;
  if (digits.length === 10) return `91${digits}`;
  return null;
}

const emptyRoute = () => ({
  from: null,
  to: null,
  fromInput: '',
  toInput: '',
  priceMin: '',
  priceMax: '',
});

function routeFromApi(route) {
  const from = route?.from || {};
  const to = route?.to || {};
  return {
    from:
      from.lat != null
        ? {
            address: from.address || '',
            lat: from.lat,
            lng: from.lng,
            place_id: from.place_id || null,
          }
        : null,
    to:
      to && to.lat != null
        ? {
            address: to.address || '',
            lat: to.lat,
            lng: to.lng,
            place_id: to.place_id || null,
          }
        : null,
    fromInput: from.address || '',
    toInput: (to && to.address) || '',
    priceMin: route?.price_min != null ? String(route.price_min) : '',
    priceMax:
      route?.price_max != null && Number(route.price_max) !== Number(route.price_min)
        ? String(route.price_max)
        : '',
  };
}

function transporterToForm(t) {
  const general = (t.routes || []).filter((r) => r.vehicle_class == null);
  const mapped = general.map(routeFromApi);
  return {
    agencyName: t.agency_name || '',
    contactName: t.contact_name || '',
    phoneNumber: String(t.phone_number || ''),
    vehicleClasses: (t.vehicle_classes || [])
      .map((c) => Number(c))
      .filter((n) => !Number.isNaN(n)),
    routes: mapped.length ? mapped : [emptyRoute()],
    phoneExtensions: (t.phone_extensions || []).map((e) => ({
      phone: String(e.phone || ''),
      label: String(e.label || ''),
    })),
    remarks: t.remarks || '',
  };
}

const blankForm = () => ({
  agencyName: '',
  contactName: '',
  phoneNumber: '',
  vehicleClasses: [],
  routes: [emptyRoute()],
  phoneExtensions: [],
  remarks: '',
});

function buildRoutePayload(r) {
  const hasMin = r.priceMin !== '' && r.priceMin != null && !Number.isNaN(Number(r.priceMin));
  const hasMax = r.priceMax !== '' && r.priceMax != null && !Number.isNaN(Number(r.priceMax));
  return {
    from_place: {
      address: r.from.address,
      latitude: r.from.lat,
      longitude: r.from.lng,
      place_id: r.from.place_id || null,
    },
    to_place:
      r.to?.lat != null
        ? {
            address: r.to.address,
            latitude: r.to.lat,
            longitude: r.to.lng,
            place_id: r.to.place_id || null,
          }
        : null,
    price_min: hasMin ? Number(r.priceMin) : null,
    price_max: hasMax ? Number(r.priceMax) : null,
  };
}

const inputStyle = {
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '8px 10px',
  width: '100%',
  fontSize: 14,
  outline: 'none',
};

function RouteEditorRows({ routes, onChange, onAdd, onRemove }) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 8,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600 }}>Routes & price range *</div>
        <button
          type="button"
          onClick={onAdd}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#2563eb',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          + Add route
        </button>
      </div>
      {routes.map((row, index) => (
        <div
          key={index}
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: 12,
            marginBottom: 10,
            background: '#fafafa',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: '#4b5563' }}>
              Route {index + 1}
            </span>
            {routes.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(index)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: '#dc2626',
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                Remove
              </button>
            )}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>From *</div>
              <PlacesAutocompleteInput
                value={row.fromInput || ''}
                onChange={(text) => onChange(index, { fromInput: text, from: null })}
                onPlaceSelect={(place) =>
                  onChange(index, { from: place, fromInput: place?.address || '' })
                }
                placeholder="Type city / area..."
                inputStyle={inputStyle}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>To (optional)</div>
              <PlacesAutocompleteInput
                value={row.toInput || ''}
                onChange={(text) => onChange(index, { toInput: text, to: null })}
                onPlaceSelect={(place) =>
                  onChange(index, { to: place, toInput: place?.address || '' })
                }
                placeholder="Optional destination..."
                inputStyle={inputStyle}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Price / Min (optional)
              <input
                type="number"
                min="0"
                style={inputStyle}
                value={row.priceMin}
                onChange={(e) => onChange(index, { priceMin: e.target.value })}
              />
            </label>
            <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
              Max (optional)
              <input
                type="number"
                min="0"
                style={inputStyle}
                value={row.priceMax}
                onChange={(e) => onChange(index, { priceMax: e.target.value })}
              />
            </label>
          </div>
        </div>
      ))}
    </div>
  );
}

const TransporterDetailsModal = ({
  isOpen = false,
  transporterId = null,
  transporterPhone = null,
  onClose = () => {},
  onSaved = () => {},
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState(transporterId);
  const [form, setForm] = useState(blankForm());
  const [formError, setFormError] = useState('');

  const loadTransporter = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setFormError('');
    try {
      let url = '';
      if (transporterId) {
        url = `${API_BASE}/api/v1/ops/transporters/${transporterId}`;
      } else if (transporterPhone) {
        const cleanPhone = String(transporterPhone).replace(/\D/g, '');
        url = `${API_BASE}/api/v1/ops/transporters/by-phone/${cleanPhone}`;
      }

      if (!url) {
        setForm(blankForm());
        setLoading(false);
        return;
      }

      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Transporter not found');
      }

      const t = data.transporter;
      if (t) {
        setActiveId(t.id);
        setForm(transporterToForm(t));
      } else {
        throw new Error('No transporter data found');
      }
    } catch (e) {
      console.error('Failed to load transporter:', e);
      setFormError(e.message || 'Failed to load transporter');
    } finally {
      setLoading(false);
    }
  }, [isOpen, transporterId, transporterPhone]);

  useEffect(() => {
    if (isOpen) {
      setActiveId(transporterId);
      loadTransporter();
    } else {
      setForm(blankForm());
      setFormError('');
      setActiveId(null);
    }
  }, [isOpen, transporterId, transporterPhone, loadTransporter]);

  if (!isOpen) return null;

  const toggleClass = (cls) => {
    setForm((prev) => {
      const has = prev.vehicleClasses.includes(cls);
      return {
        ...prev,
        vehicleClasses: has
          ? prev.vehicleClasses.filter((c) => c !== cls)
          : [...prev.vehicleClasses, cls].sort((a, b) => a - b),
      };
    });
  };

  const updateRoute = (index, patch) => {
    setForm((prev) => {
      const routes = [...prev.routes];
      routes[index] = { ...routes[index], ...patch };
      return { ...prev, routes };
    });
  };

  const validateRoutesList = (list) => {
    if (!list.length) return 'At least one route is required';
    for (let i = 0; i < list.length; i++) {
      const r = list[i];
      if (!r.from?.address) return `Route ${i + 1}: please pick a valid From place`;
      if (
        r.priceMin !== '' &&
        r.priceMax !== '' &&
        r.priceMin != null &&
        r.priceMax != null &&
        Number(r.priceMax) < Number(r.priceMin)
      ) {
        return `Route ${i + 1}: max price cannot be less than min price`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    setFormError('');
    if (!form.agencyName.trim()) {
      setFormError('Agency name is required');
      return;
    }
    if (!form.contactName.trim()) {
      setFormError('Contact person name is required');
      return;
    }
    const phoneNorm = normalizePhoneForDb(form.phoneNumber);
    if (!phoneNorm) {
      setFormError('Enter a valid 10-digit mobile number (or 91XXXXXXXXXX)');
      return;
    }
    if (!form.vehicleClasses.length) {
      setFormError('Select at least one vehicle class');
      return;
    }
    const routeErr = validateRoutesList(form.routes);
    if (routeErr) {
      setFormError(routeErr);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        agency_name: form.agencyName.trim(),
        contact_name: form.contactName.trim(),
        phone_number: phoneNorm,
        vehicle_classes: form.vehicleClasses,
        routes: form.routes.map((r) => buildRoutePayload(r)),
        phone_extensions: (form.phoneExtensions || [])
          .filter((e) => e.phone && e.phone.trim())
          .map((e) => ({
            phone: e.phone.trim(),
            label: (e.label || '').trim(),
          })),
        remarks: form.remarks ? form.remarks.trim() : null,
      };

      const url = activeId
        ? `${API_BASE}/api/v1/ops/transporters/${activeId}`
        : `${API_BASE}/api/v1/ops/transporters/onboard`;
      const method = activeId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || data.message || 'Failed to save transporter');
      }

      toast.success(activeId ? 'Transporter updated successfully' : 'Transporter onboarded successfully');
      if (onSaved) onSaved(data.transporter);
      onClose();
    } catch (err) {
      setFormError(err.message || 'Failed to save transporter');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.5)',
        zIndex: 2000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '32px 16px',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          maxWidth: 620,
          width: '100%',
          padding: 24,
          boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            gap: 12,
            borderBottom: '1px solid #f1f5f9',
            paddingBottom: 12,
          }}
        >
          <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#1e293b' }}>
            {activeId ? 'Edit Transporter' : 'Onboard Transporter'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 24,
              cursor: 'pointer',
              lineHeight: 1,
              color: '#64748b',
            }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
            <i className="fa fa-spinner fa-spin" style={{ fontSize: 24, marginBottom: 8 }} />
            <div>Loading transporter details...</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600, color: '#334155' }}>
              Agency name *
              <input
                style={inputStyle}
                value={form.agencyName}
                onChange={(e) => setForm({ ...form, agencyName: e.target.value })}
                placeholder="Agency / company name"
              />
            </label>

            <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600, color: '#334155' }}>
              Contact person name *
              <input
                style={inputStyle}
                value={form.contactName}
                onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                placeholder="Person name"
              />
            </label>

            <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600, color: '#334155' }}>
              Phone number *
              <input
                style={inputStyle}
                value={form.phoneNumber}
                onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })}
                placeholder="91XXXXXXXXXX or 10-digit phone"
              />
            </label>

            {/* Phone Extensions */}
            <div style={{ display: 'grid', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Phone extensions (optional)</div>
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      phoneExtensions: [...(prev.phoneExtensions || []), { phone: '', label: '' }],
                    }))
                  }
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#2563eb',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  + Add extension
                </button>
              </div>
              {(!form.phoneExtensions || form.phoneExtensions.length === 0) ? (
                <div style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic', marginBottom: 4 }}>
                  No extensions added. Click "+ Add extension" to add contact numbers.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 8, marginBottom: 4 }}>
                  {form.phoneExtensions.map((ext, idx) => (
                    <div key={`ext-${idx}`} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        placeholder="Phone (10 or 12 digits)"
                        style={{ ...inputStyle, flex: 1 }}
                        value={ext.phone}
                        onChange={(e) => {
                          const updated = [...form.phoneExtensions];
                          updated[idx].phone = e.target.value;
                          setForm({ ...form, phoneExtensions: updated });
                        }}
                      />
                      <input
                        placeholder="Label (e.g. manager)"
                        style={{ ...inputStyle, flex: 1 }}
                        value={ext.label}
                        onChange={(e) => {
                          const updated = [...form.phoneExtensions];
                          updated[idx].label = e.target.value;
                          setForm({ ...form, phoneExtensions: updated });
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const updated = form.phoneExtensions.filter((_, i) => i !== idx);
                          setForm({ ...form, phoneExtensions: updated });
                        }}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          padding: '4px 8px',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Vehicle classes */}
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#334155' }}>
                Vehicle classes *
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {CLASS_OPTIONS.map((cls) => {
                  const selected = form.vehicleClasses.includes(cls);
                  return (
                    <button
                      key={cls}
                      type="button"
                      onClick={() => toggleClass(cls)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 999,
                        border: selected ? '1px solid #2563eb' : '1px solid #d1d5db',
                        background: selected ? '#eff6ff' : '#fff',
                        color: selected ? '#1d4ed8' : '#374151',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      Class {cls}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Remarks */}
            <label style={{ display: 'grid', gap: 4, fontSize: 13, fontWeight: 600, color: '#334155' }}>
              Remarks (optional)
              <textarea
                style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                value={form.remarks || ''}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                placeholder="Notes, summary, or other details"
              />
            </label>

            {/* Routes */}
            <RouteEditorRows
              routes={form.routes}
              onChange={updateRoute}
              onAdd={() =>
                setForm((prev) => ({ ...prev, routes: [...prev.routes, emptyRoute()] }))
              }
              onRemove={(index) =>
                setForm((prev) => ({
                  ...prev,
                  routes:
                    prev.routes.length > 1
                      ? prev.routes.filter((_, i) => i !== index)
                      : [emptyRoute()],
                }))
              }
            />

            {formError && (
              <div style={{ color: '#b91c1c', fontSize: 13, background: '#fee2e2', padding: '8px 12px', borderRadius: 6 }}>
                {formError}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 14 }}>
              <button
                type="button"
                disabled={saving}
                onClick={onClose}
                style={{
                  padding: '9px 16px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: 13,
                  color: '#475569',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleSubmit}
                style={{
                  padding: '9px 18px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#2563eb',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: saving ? 'wait' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {saving && <i className="fa fa-spinner fa-spin" />}
                {saving
                  ? 'Saving…'
                  : activeId
                    ? 'Save changes'
                    : 'Save transporter'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TransporterDetailsModal;
